/**
 * 历史记录队列（最新记录在数组头部）
 * - 支持相等比较函数（用于判断重复），重复时先删除原有记录，再插入新记录到最前面
 * - 超出 maxSize 时自动删除最旧的记录
 * - 支持按索引删除（0 = 最新）、按内容删除
 * - 自动持久化到 localStorage
 */
class HistoryRecord {
  /**
   * @param {number} maxSize            最大保存条数（非负整数）
   * @param {string} storageKey         本地存储键名，默认 'history_queue'
   * @param {boolean} autoSave          是否每次修改后自动保存，默认 true
   * @param {Function|null} equalityFn  相等比较函数 (a, b) => boolean，用于判断两条记录是否重复
   *                                    默认使用严格相等 (a, b) => a === b
   *                                    传入 null 表示不检查重复（允许相同记录共存）
   */
  constructor(
    maxSize = 20,
    storageKey = "history_queue",
    autoSave = true,
    equalityFn = (a, b) => a === b
  ) {
    if (!Number.isInteger(maxSize) || maxSize < 0) {
      throw new Error("maxSize must be a non-negative integer");
    }
    this._maxSize = maxSize;
    this._storageKey = storageKey;
    this._autoSave = autoSave;
    this._equalityFn = typeof equalityFn === "function" ? equalityFn : null;
    this._records = []; // 索引0 = 最新，索引length-1 = 最旧

    this._loadFromStorage();
    this._truncateToMaxSize();
    if (this._autoSave) this._saveToStorage();
  }

  // ------------------- 私有方法 -------------------
  _loadFromStorage() {
    if (typeof localStorage === "undefined") {
      console.warn("localStorage 不可用，数据不会持久化");
      return;
    }
    const raw = localStorage.getItem(this._storageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          this._records = parsed;
        }
      } catch (e) {
        console.error("解析本地存储的历史记录失败", e);
      }
    }
  }

  _saveToStorage() {
    if (!this._autoSave) return;
    if (typeof localStorage === "undefined") {
      console.warn("localStorage 不可用，无法保存");
      return;
    }
    try {
      localStorage.setItem(this._storageKey, JSON.stringify(this._records));
    } catch (e) {
      console.error("保存历史记录到 localStorage 失败", e);
    }
  }

  _truncateToMaxSize() {
    if (this._maxSize === 0) {
      this._records = [];
    } else {
      while (this._records.length > this._maxSize) {
        this._records.pop(); // 移除最旧（尾部）
      }
    }
  }

  _afterModify() {
    this._truncateToMaxSize();
    if (this._autoSave) this._saveToStorage();
  }

  // ------------------- 公开 API -------------------
  /**
   * 设置相等比较函数（用于判断重复）
   * @param {Function|null} fn - 比较函数 (a, b) => boolean，传 null 关闭重复检查
   */
  setEqualityFn(fn) {
    this._equalityFn = typeof fn === "function" ? fn : null;
  }

  /**
   * 添加一条记录（插入到最前面）
   * - 如果队列中存在与 newRecord 相等的记录（由 equalityFn 判定），则先删除那条旧记录
   * - 然后将新记录插入到数组头部（最新位置）
   * - 若 equalityFn 为 null，则不检查重复，直接插入
   * @param {any} record
   * @returns {boolean} 是否成功添加（maxSize=0 时返回 false）
   */
  add(record) {
    if (this._maxSize === 0) return false;

    // 如果提供了相等比较函数，则查找并删除重复项（只删除第一个匹配的）
    if (this._equalityFn) {
      const duplicateIndex = this._records.findIndex((item) =>
        this._equalityFn(item, record)
      );
      if (duplicateIndex !== -1) {
        this._records.splice(duplicateIndex, 1); // 删除原有的重复记录
      }
    }

    // 插入到头部（最新）
    this._records.unshift(record);
    this._afterModify();
    return true;
  }

  /**
   * 删除指定索引位置的记录（0 = 最新记录）
   * @param {number} index
   * @returns {any|null}
   */
  removeAt(index) {
    if (index >= 0 && index < this._records.length) {
      const removed = this._records.splice(index, 1)[0];
      this._afterModify();
      return removed;
    }
    return null;
  }

  /**
   * 删除与目标值相等的第一条记录（从最新开始搜索）
   * @param {any} target
   * @param {Function} compareFn - 比较函数，默认使用严格相等
   * @returns {any|null}
   */
  remove(target, compareFn = (a, b) => a === b) {
    const index = this._records.findIndex((item) => compareFn(item, target));
    if (index !== -1) {
      const removed = this._records.splice(index, 1)[0];
      this._afterModify();
      return removed;
    }
    return null;
  }

  /**
   * 设置新的最大条数限制
   * @param {number} newMaxSize
   */
  setMaxSize(newMaxSize) {
    if (!Number.isInteger(newMaxSize) || newMaxSize < 0) {
      throw new Error("maxSize must be a non-negative integer");
    }
    this._maxSize = newMaxSize;
    this._afterModify();
  }

  /**
   * 清空所有历史记录
   */
  clear() {
    this._records = [];
    this._afterModify();
  }

  /**
   * 手动保存到 localStorage
   */
  save() {
    this._saveToStorage();
  }

  /**
   * 从 localStorage 重新加载（放弃未保存的更改）
   */
  reload() {
    this._records = [];
    this._loadFromStorage();
    this._truncateToMaxSize();
    if (this._autoSave) this._saveToStorage();
  }

  /**
   * 获取所有记录（从最新到最旧）
   * @returns {Array}
   */
  getAll() {
    return [...this._records];
  }

  /**
   * 获取记录条数
   * @returns {number}
   */
  size() {
    return this._records.length;
  }

  /**
   * 获取当前最大条数限制
   * @returns {number}
   */
  getMaxSize() {
    return this._maxSize;
  }

  /**
   * 获取最新 N 条记录
   * @param {number} n
   * @returns {Array}
   */
  getLatest(n) {
    return this._records.slice(0, n);
  }

  /**
   * 获取最旧 N 条记录
   * @param {number} n
   * @returns {Array}
   */
  getOldest(n) {
    const start = Math.max(0, this._records.length - n);
    return this._records.slice(start);
  }

  /**
   * 静态方法：清除指定键的本地存储
   * @param {string} storageKey
   */
  static clearStorage(storageKey = "history_queue") {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(storageKey);
    }
  }
}

export default HistoryRecord;
